import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import history from "../../routes/History";
import { fetchCompanyPortalCurrentUser } from "../../network/apis/companyPortalRegistration";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import {
  getCompanyRegistrationStepRoute,
  getResolvedCompanyPortalSession,
  isCompanyRegistrationIncompleteState,
  persistCompanyPortalSession,
} from "../../utils/companyRegistration";
import { AUTH_CONTEXT_TYPE } from "../../utils/authPortal";
import PendingApproval from "./PendingApproval";
 

/**
 * `/company/pending-approval` — refreshes user via GET /company_users/me; when
 * `company.status` is no longer `pending`, redirects (events or canonical signup/login step).
 * Header logout: `performCompanyPendingApprovalLogout()` (same module) → DELETE /sessions + clear client.
 */
const CompanyPendingApprovalPage = () => {
  const emailFromStore = useSelector((state) => state.auth.login?.email ?? "");
  const [emailDisplay, setEmailDisplay] = useState(emailFromStore);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const me = await fetchCompanyPortalCurrentUser();
        if (cancelled) return;

        const session = getResolvedCompanyPortalSession();
        const merged = { ...session, ...me };
        persistCompanyPortalSession(merged);

        if (merged.company?.status !== "pending") {
          const next = isCompanyRegistrationIncompleteState(merged)
            ? getCompanyRegistrationStepRoute(merged)
            : ROUTE_PATHS.companyHome;
          history.replace(next);
          return;
        }

        const nextEmail =
          merged.email ??
          merged.email_address ??
          session.email ??
          "";
        if (nextEmail) {
          setEmailDisplay(String(nextEmail));
        }
      } catch {
        /* 401 → interceptor; other failures keep current shell + session */
      }
    };

    refresh();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PendingApproval status="pending" email={emailDisplay || emailFromStore} />
  );
};

CompanyPendingApprovalPage.propTypes = {
  type: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

CompanyPendingApprovalPage.defaultProps = {
  type: AUTH_CONTEXT_TYPE.COMPANY,
};

export default CompanyPendingApprovalPage;
